/**
 * Seeded test credentials for local development.
 *
 * **Never imported in production builds.** The page that renders these
 * is gated by `import.meta.env.DEV`, and the route is mounted only when
 * the dev flag is set. Even so, treat this file as containing real
 * passwords for the seeded fixtures -- if those credentials are ever
 * reused in a deployed environment, this file is a leak.
 *
 * Source of truth lives in the sprout-api seed scripts:
 *   - scripts/create_super_admin.py (platform.admin)
 *   - scripts/seed_test_data.py     (Hogwarts staff)
 *   - scripts/seed_protea_school.py (Protea staff)
 *   - scripts/seed_protea_guardians.py (Protea guardian portal)
 *
 * Re-sync this list whenever the seeds change.
 */

export type CredentialRole =
  | 'super_admin'
  | 'principal'
  | 'teacher'
  | 'guardian';

export type LoginPath = 'staff' | 'guardian';

export interface DevCredential {
  /** Display name (first + last). */
  name: string;
  username: string;
  password: string;
  role: CredentialRole;
  /** Description of the user's school + role -- for the card subtitle. */
  context: string;
  /** Notable behaviour / why this user is interesting for testing. */
  hint?: string;
  /** Which login flow the credentials use. Staff hit /login; guardians
   * have a separate portal at /guardian/login. */
  loginPath: LoginPath;
  /** For guardians: list of children's names. Populated when the
   * guardian has more than one child so the page can flag the
   * multi-child case visually -- this is the only way to test the
   * portal's child selector and multi-card flows. */
  children?: string[];
}

export interface CredentialGroup {
  title: string;
  description: string;
  credentials: DevCredential[];
}

export const DEV_CREDENTIALS: CredentialGroup[] = [
  {
    title: 'Platform Super-Admin',
    description:
      'Cross-tenant operator with access to /admin/orgs and tenant impersonation. Sits in the dedicated PLATFORM org so they\'re not inside any school.',
    credentials: [
      {
        name: 'Platform Admin',
        username: 'platform.admin',
        password: 'platform-admin-2026',
        role: 'super_admin',
        context: 'PLATFORM (dedicated)',
        hint: 'Only role that sees /admin/orgs and the "View as this org" impersonation button.',
        loginPath: 'staff',
      },
    ],
  },
  {
    title: 'Protea Primary School',
    description:
      'South African demo school with full CAPS curriculum + 26 children + 105 observations + 62 assessments. Best for testing the AI report draft and CAPS-aware suggestions.',
    credentials: [
      {
        name: 'Lindiwe Mkhize',
        username: 'lindiwe.mkhize',
        password: 'protea123',
        role: 'principal',
        context: 'Protea Primary · Principal',
        hint: 'Has admin permissions across the org. Can create observations, view all reports, manage users.',
        loginPath: 'staff',
      },
      {
        name: 'Nomsa Dube',
        username: 'nomsa.dube',
        password: 'protea123',
        role: 'teacher',
        context: 'Protea Primary · Grade R teacher',
        loginPath: 'staff',
      },
      {
        name: 'Jan Pretorius',
        username: 'jan.pretorius',
        password: 'protea123',
        role: 'teacher',
        context: 'Protea Primary · Grade 1-2 teacher',
        loginPath: 'staff',
      },
      {
        name: 'Priya Govender',
        username: 'priya.govender',
        password: 'protea123',
        role: 'teacher',
        context: 'Protea Primary · Grade 3 teacher',
        loginPath: 'staff',
      },
    ],
  },
  {
    title: 'Hogwarts (Magical Demo)',
    description:
      'Test data with Harry Potter characters. Good for end-to-end smoke and Playwright fixtures. No CAPS curriculum -- the suggested-outcomes feature returns empty here.',
    credentials: [
      {
        name: 'Albus Dumbledore',
        username: 'albus.dumbledore',
        password: 'magic123',
        role: 'principal',
        context: 'Hogwarts · Headmaster',
        hint: 'Default principal user across the E2E test suite.',
        loginPath: 'staff',
      },
      {
        name: 'Filius Flitwick',
        username: 'filius.flitwick',
        password: 'magic123',
        role: 'teacher',
        context: 'Hogwarts · Charms professor',
        loginPath: 'staff',
      },
      {
        name: 'Severus Snape',
        username: 'severus.snape',
        password: 'magic123',
        role: 'teacher',
        context: 'Hogwarts · Potions master',
        loginPath: 'staff',
      },
      {
        name: 'Remus Lupin',
        username: 'remus.lupin',
        password: 'magic123',
        role: 'teacher',
        context: 'Hogwarts · DADA professor',
        loginPath: 'staff',
      },
      {
        name: 'Minerva McGonagall',
        username: 'minerva.mcgonagall',
        password: 'magic123',
        role: 'teacher',
        context: 'Hogwarts · Transfiguration professor',
        loginPath: 'staff',
      },
    ],
  },
  {
    title: 'Protea Guardian Portal',
    description:
      'Parent portal logins for Protea families. Different login flow at /guardian/login -- guardians cannot log into the staff app. Zanele Nkosi and Pieter van der Merwe each have multiple children, so they exercise the multi-card / child-selector flows.',
    credentials: [
      {
        name: 'Zanele Nkosi',
        username: 'zanele.nkosi',
        password: 'protea123',
        role: 'guardian',
        context: 'Mother of Thandi Nkosi (Grade R) + Bongani Cele (Grade 1)',
        hint: 'Multi-child guardian -- exercises the portal\'s child selector and cross-grade card layout.',
        loginPath: 'guardian',
        children: ['Thandi Nkosi', 'Bongani Cele'],
      },
      {
        name: 'Sipho Dlamini Sr.',
        username: 'sipho.dlamini.sr',
        password: 'protea123',
        role: 'guardian',
        context: 'Parent of Sipho Dlamini',
        loginPath: 'guardian',
      },
      {
        name: 'Nomvula Molefe',
        username: 'nomvula.molefe',
        password: 'protea123',
        role: 'guardian',
        context: 'Parent of Lerato Molefe',
        loginPath: 'guardian',
      },
      {
        name: 'Pieter van der Merwe',
        username: 'pieter.vandermerwe',
        password: 'protea123',
        role: 'guardian',
        context: 'Father of Pieter Jr. (Grade R) + step-father of Mia Erasmus (Grade 2)',
        hint: 'Multi-child guardian with mixed relationship types -- tests per-child relationship label rendering.',
        loginPath: 'guardian',
        children: ['Pieter van der Merwe', 'Mia Erasmus'],
      },
      {
        name: 'Fatima Hendricks',
        username: 'fatima.hendricks',
        password: 'protea123',
        role: 'guardian',
        context: 'Parent of Yusuf Hendricks',
        loginPath: 'guardian',
      },
      {
        name: 'Thandiwe Zulu',
        username: 'thandiwe.zulu',
        password: 'protea123',
        role: 'guardian',
        context: 'Parent of Amahle Zulu',
        loginPath: 'guardian',
      },
      {
        name: 'Sarah Botha',
        username: 'sarah.botha',
        password: 'protea123',
        role: 'guardian',
        context: 'Parent of Anika Botha',
        loginPath: 'guardian',
      },
      {
        name: 'Kagiso Mahlangu Sr.',
        username: 'kagiso.mahlangu.sr',
        password: 'protea123',
        role: 'guardian',
        context: 'Parent of Kagiso Mahlangu',
        loginPath: 'guardian',
      },
    ],
  },
];
